import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { useGetOnboardingQuery, useGetStateQuery } from '../../../features/platform/platformApi';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { usePlatformState } from '../../../features/platform/platformStateSlice';

/* ------------------------------------------------------------------ *
 * Platform Admin — State onboarding.
 * The six-step checklist that takes a state from empty to live,
 * wired to the Platform Admin API for the active state.
 * ------------------------------------------------------------------ */

/** Destination per step number (1-based). Step 1 opens the source-picker modal instead. */
const STEP_GO: Record<number, string> = {
  2: '/admin/templates',
  3: '/admin/departments',
  4: '/admin/work-queues',
  5: '/admin/work-queues',
  6: '/admin/departments',
};

/** The three standard sources imported in step 1. */
const SOURCES: { key: string; name: string; desc: string }[] = [
  { key: 'LGD', name: 'LGD', desc: 'Local Government Directory — districts, sub-districts, villages, blocks, gram panchayats, urban bodies and wards.' },
  { key: 'ECI', name: 'ECI', desc: 'Election Commission of India — parliamentary & assembly constituencies and polling booths.' },
  { key: 'SEC', name: 'SEC', desc: 'State Election Commission — local bodies and their wards.' },
];

export default function StateOnboardingPage() {
  const navigate = useNavigate();
  const { code, name } = usePlatformState();
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const startImport = () => {
    if (!picked) return;
    message.success(`Importing ${picked} for ${name}… (sample)`);
    setSourcesOpen(false);
    setPicked(null);
  };

  const { data, isLoading } = useGetOnboardingQuery(code);
  const { data: stateData } = useGetStateQuery(code);

  // Hide the "Review and go live" step — the go-live flow was removed.
  const steps = (data?.steps ?? []).filter((s) => !/go\s*live/i.test(s.title ?? ''));
  const totalSteps = steps.length || 1;
  const stepsDone = steps.filter((s) => s.done).length;
  const clients = stateData?.clients ?? 0;
  const pct = Math.round((stepsDone / totalSteps) * 100);

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs items={[{ label: 'All states', to: '/admin/overview' }, { label: 'Onboarding' }]} />
      {/* Header card */}
      <section className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div className="flex min-w-0 flex-1 basis-[460px] flex-col gap-1.5">
          <span className="text-xs font-semibold text-brand">PLATFORM ADMIN · STATE ONBOARDING</span>
          <h1 className="text-2xl font-semibold text-ink">Onboarding {name}</h1>
          <span className="text-[13px] text-ink-secondary">
            {stepsDone} of {totalSteps} steps done · {clients} client(s)
          </span>
          <span className="block h-2 max-w-[520px] overflow-hidden rounded-full bg-muted-bg">
            <span className="block h-2 rounded-full bg-brand" style={{ width: `${pct}%` }} />
          </span>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-[34px] font-bold text-brand">{pct}%</span>
        </div>
      </section>

      {isLoading && <span className="text-sm text-ink-secondary">Loading…</span>}

      {/* Steps */}
      {steps.map((s, i) => {
        const status = s.done ? 'Done' : s.current ? 'In progress' : 'Not started';
        const statusCls = s.done
          ? 'bg-brand-shade text-brand'
          : s.current
            ? 'bg-[#fffbeb] text-[#b45309]'
            : 'bg-muted-bg text-ink-secondary';
        const barWidth = s.done ? 100 : s.current ? 50 : 0;
        return (
          <section
            key={s.step}
            className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3.5 rounded-xl border border-line/30 bg-white p-5 shadow-card"
          >
            <span className="flex size-10 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
              {s.step ?? i + 1}
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <b className="text-[15px] text-ink">{s.title}</b>
                <span className={['rounded-full px-2.5 py-0.5 text-xs font-semibold', statusCls].join(' ')}>
                  {status}
                </span>
              </div>
              <span className="text-[13px] text-ink-secondary">{s.desc}</span>
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="block h-1.5 w-[200px] overflow-hidden rounded-full bg-muted-bg">
                  <span
                    className={['block h-1.5 rounded-full', s.done ? 'bg-brand' : 'bg-[#8a4b0f]'].join(' ')}
                    style={{ width: `${barWidth}%` }}
                  />
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                s.step === 1 ? navigate('/admin/import-sources') : navigate(STEP_GO[s.step] ?? '/admin/onboarding')
              }
              className="h-10 shrink-0 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              {s.action}
            </button>
          </section>
        );
      })}

      {/* Step 1 — choose which standard source to import */}
      <Modal
        open={sourcesOpen}
        title="Import standard sources"
        onCancel={() => {
          setSourcesOpen(false);
          setPicked(null);
        }}
        okText="Start import"
        okButtonProps={{ disabled: !picked }}
        onOk={startImport}
      >
        <div className="flex flex-col gap-3 py-1">
          <p className="text-[13px] text-ink-secondary">Which data are you uploading for {name}?</p>
          <div className="flex flex-col gap-2.5">
            {SOURCES.map((src) => {
              const on = picked === src.key;
              return (
                <button
                  key={src.key}
                  type="button"
                  onClick={() => setPicked(src.key)}
                  className={[
                    'flex flex-col gap-1 rounded-lg border p-3.5 text-left transition-colors',
                    on ? 'border-2 border-brand bg-brand-shade/60' : 'border-line bg-white hover:bg-brand-shade/40',
                  ].join(' ')}
                >
                  <span className="text-[15px] font-bold text-ink">{src.name}</span>
                  <span className="text-xs text-ink-secondary">{src.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
}
